import { groupBy } from 'lodash';

import { BORDER_CROSSING, CANAL_TRANSIT, MODE_TRANSFER, TRANSFER_STOP } from '@/lib/constants';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { RouteLinkSegmentEventApiV4Interface, SegmentModeType } from '@/types/api-types';
import { convertModeVerb, formatRouteLocation } from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';

export const PRESENTATION_EVENTS = [CANAL_TRANSIT, TRANSFER_STOP, BORDER_CROSSING] as const;
type PresentationEventType = (typeof PRESENTATION_EVENTS)[number];

const EVENT_TYPE_LABEL_MAP: Record<PresentationEventType, string> = {
  [TRANSFER_STOP]: 'Stops',
  [BORDER_CROSSING]: 'Border crossing',
  [CANAL_TRANSIT]: 'Transit canal',
};

export const getSequenceDescription = (
  {
    fromId,
    toId,
  }: {
    fromId: string;
    toId?: string;
  },
  locations: LocationDictionaryModel,
): string => {
  const from = formatRouteLocation(fromId, locations);

  if (toId) {
    const to = formatRouteLocation(toId, locations);
    return `${from.name} to ${to.name}`;
  }

  return from.name;
};

/**
 * Will take all events and generate a string like (Stops at MEL, Canal traversal at PANAMA)
 */
export const getEventsDescription = (
  { events }: { events: RouteLinkSegmentEventApiV4Interface[] },
  locations: LocationDictionaryModel,
): string => {
  const groupedEvents = groupBy(events, 'type');
  const groupedEventStrings = Object.entries(groupedEvents).map(([eventType, eventGroup]) => {
    const eventLocations = eventGroup.map((event) => {
      const location = formatRouteLocation(event.locationId, locations);
      return getPreferredIdentifier(location.identifiers)?.value;
    });
    const eventLabel = EVENT_TYPE_LABEL_MAP[eventType as PresentationEventType];

    return `${eventLabel} at ${eventLocations.join(', ')}`;
  });

  return ` (${groupedEventStrings.join(', ')})`;
};
export const getSequenceTitle = (
  {
    fromId,
    toId,
    routeType,
    transferType,
  }: {
    fromId: string;
    toId?: string;
    routeType: SegmentModeType;
    transferType?: string;
  },
  locations: LocationDictionaryModel,
): string => {
  const fromPort = formatRouteLocation(fromId, locations);
  const toPort = toId ? formatRouteLocation(toId, locations) : null;
  const fromCode = fromPort ? getPreferredIdentifier(fromPort.identifiers) : null;
  const toCode = toPort ? getPreferredIdentifier(toPort.identifiers) : null;

  if (routeType === MODE_TRANSFER && fromCode) {
    if (transferType && transferType === TRANSFER_STOP) {
      return `Stops at ${fromCode.value}`;
    }
    if (transferType && transferType === CANAL_TRANSIT) {
      return `Transit canal at ${fromCode.value}`;
    }
    return `Transfer at ${fromCode.value}`;
  }

  if (fromCode && toCode) {
    return `${fromCode.value} to ${toCode.value}`;
  }

  const modePrefix = convertModeVerb(routeType, transferType);

  return `${modePrefix} ${toCode ? toCode.value : toPort?.name}`;
};
